<script lang="ts">
  import AddProductModal from "@retailer/svelte/ProductLines/Index/AddProductModal.svelte";
  import BannerComponent from "@shared/svelte/Components/Banner/Banner.svelte";
  import Header from "@retailer/svelte/ProductLines/Index/Header.svelte";
  import NoProductLinesAvailable from "@retailer/svelte/ProductLines/ProductLineContainer/NoProductLinesAvailable.svelte";
  import PaginationLarge from "@shared/svelte/Components/Pagination/Large.svelte";
  import ProductLineGrid from "@retailer/svelte/ProductLines/Index/ProductLineGrid.svelte";
  import ProductLineList from "@retailer/svelte/ProductLines/Index/ProductLineList.svelte";
  import SearchClassificationGuidance from "@retailer/svelte/ProductLines/ProductLineContainer/SearchClassificationGuidance.svelte";
  import SkeletonProductCards from "@shared/svelte/Components/ProductLines/Skeletons/SkeletonProductCards.svelte";
  import SkeletonProductListCard from "@shared/svelte/Components/ProductLines/Skeletons/SkeletonProductListCard.svelte";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";

  import getFilterParamsFromState from "@retailer/services/filter_params_from_state_params.js";
  import { AdTypes, storeCachedAds, getCachedAds } from "@shared/services/cached_ads";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { Http } from "@shared/svelte/Modules/ApiService";
  import { adCountForSortingAndLayout } from "@shared/types/ProductLinesSortOptions";
  import {
    fetchDiscountData,
    fetchSupplementalData,
  } from "@retailer/services/product_results_services";
  import { getProductListingPageNameAndValue } from "@retailer/services/page_location_helpers";
  import { restrictPreviewAccess } from "@retailer/services/preview_forbidden_flash";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  import { afterPageLoad, goto, params } from "@roxi/routify";
  import { cloneDeep, compact, isEqual, omitBy } from "lodash";

  import {
    ProductLineListingStore,
    updateProductLineListingStore,
    ProductListingLayout,
    DEFAULT_LAYOUT,
    DEFAULT_LIMIT,
  } from "@retailer/svelte/ProductLines/ProductLineContainer/stores/ProductLineListingStore";

  import type { ProductLine } from "@shared/types/ProductLine";
  import type { ProductIndexBanner } from "@shared/types/ProductIndexBanner";
  import type { SearchClassification } from "@shared/types/SearchClassification";
  import { onDestroy } from "svelte";

  const { trackClick } = EventTracking;

  export let Cart: any = null;
  export let includeExternalAds: boolean = false;
  export let isHome: boolean = false;
  export let dedupeOrganicResultsFromAds: boolean = true;
  export let mobileMode: boolean;
  export let overrideStateParams: any = null;
  export let overridePath = null;
  export let productIndexBannersRes = [];
  export let shoppableUnitRes = {};
  export let stateParams;

  const filterAndPaginationPath = overridePath ? overridePath : "/product_listing";

  let currentCarrotBannerAdIds = [];
  let aboveGridBanner: ProductIndexBanner = null;
  let gridBanners: ProductIndexBanner[] = [];
  let perPage: number = DEFAULT_LIMIT;

  let showAddProductModal = false;
  let pagination = null;
  let productLines: ProductLine[] = [];
  let loadingProductLines = true;
  let page = Number(stateParams.page || 1);
  let productListLayout: ProductListingLayout = DEFAULT_LAYOUT;

  const bannersChanged = (): boolean => {
    const newCarrotBannerAdsIds: string[] = (productIndexBannersRes || [])
      .map(res => res.banners?.map(banner => banner.object_tracking_id || banner.ad_id))
      .flat();

    if (!isEqual(currentCarrotBannerAdIds, newCarrotBannerAdsIds)) {
      currentCarrotBannerAdIds = newCarrotBannerAdsIds;
      return true;
    }
    return false;
  };

  $: {
    if (bannersChanged()) {
      if (stagedRolloutCheck("kevel_ads")) {
        let banners = productIndexBannersRes;

        if (banners.length > 0) {
          aboveGridBanner = { banners: [banners[0]] };
          if (banners.length > 1) {
            gridBanners = [{ banners: banners.slice(1) }];
          } else {
            gridBanners = [];
          }
        } else {
          aboveGridBanner = null;
          gridBanners = [];
        }

      } else {
        aboveGridBanner = productIndexBannersRes?.find(b => b.above_grid);
        gridBanners = productIndexBannersRes?.filter(b => !b.above_grid);
      }
    }
  }

  const unsubscribe = ProductLineListingStore.subscribe(store => {
    productListLayout = store.productListLayout as ProductListingLayout;
    perPage = store.productListLimit;
  });

  onDestroy(() => {
    unsubscribe();
  });

  const runShowAddProductModal = entities => {
    if (restrictPreviewAccess()) {
      return;
    } else {
      trackClick("plp_add_product", entities);
      showAddProductModal = true;
    }
  };

  const createPagination = totalEntries => {
    page = Number(stateParams.page || 1);
    return {
      perPage: perPage,
      page: page,
      totalEntries: totalEntries,
      goToPage: n => {
        Object.assign(stateParams, { ...stateParams, page: n });
        $goto(filterAndPaginationPath, stateParams);
      },
    };
  };

  const resetScroll = () => {
    setTimeout(() => {
      window.scrollTo({ left: 0, top: 0, behavior: "smooth" });
    }, 50);
  };

  const storeScroll = () => {
    localStorage.setItem(
      "product_listing_scroll_position",
      window.location.search + "-" + window.scrollY,
    );
  };

  const scrollToStoredPosition = () => {
    if (isHome) {
      return;
    }

    const storedValue = localStorage.getItem("product_listing_scroll_position");
    // If there is no previous stored scroll position, we should not attempt to auto-scroll
    if (!storedValue) {
      return;
    }

    const storedObject = storedValue.split("-");
    // Only auto-scroll if the current search params match exactly those of the previously stored position
    if (storedObject[0] == window.location.search) {
      setTimeout(() => {
        window.scrollTo({ left: 0, top: parseInt(storedObject[1]), behavior: "smooth" });
      }, 50);
    }

    localStorage.removeItem("product_listing_scroll_position");
  };

  const setupProductLines = async (data, resp) => {
    const searchRedirect = data?.redirect_url;
    if (searchRedirect) {
      $goto(searchRedirect);
    }

    let total = 0;
    if (resp.status === 500 || resp.status === 403 || !data || !data.filter) {
      productLines = [];
    } else {
      const filterParams = getFilterParamsFromState(stateParams);

      await fetchDiscountData(data);

      productLines = data;

      const ads = productLines.filter(p => p.is_paid);
      const organic = productLines.filter(p => !p.is_paid);
      if (ads.length > 0) {
        storeCachedAds(AdTypes.Product, ads, filterParams);
      }

      const numberOfAds = adCountForSortingAndLayout(stateParams?.product_sort, productListLayout);
      productLines = getCachedAds(AdTypes.Product, filterParams, numberOfAds).concat(organic);

      fetchSupplementalData(productLines);
      total =
        typeof resp.headers === "function"
          ? resp.headers("X-Pagination-Entries")
          : resp.headers.get("X-Pagination-Entries");
    }
    pagination = createPagination(total);
    updateProductLineListingStore(n => {
      n.productsFound = productLines.length > 0;
      n.productLines = productLines;
      return n;
    });
    loadingProductLines = false;
    scrollToStoredPosition();
  };

  const resetAccordionStates = (params, page) => {
    const currentParamKey = compact([params.page_name, params.page_value, params.slug, page]).join(
      "-",
    );
    const storedParamKey = $ProductLineListingStore.accordionStatesParamsKey;
    if (currentParamKey !== storedParamKey) {
      updateProductLineListingStore(n => {
        n.listViewAccordionStates = {};
        n.accordionStatesParamsKey = currentParamKey;
        return n;
      });
    }
  };

  const loadProductLines = params => {
    params = cloneDeep(params);
    loadingProductLines = true;
    productLines = [];
    page = params.page || 1;
    params.limit = perPage;
    params.offset = perPage * ((page || 1) - 1);

    const { pageName, pageValue } = getProductListingPageNameAndValue(params);
    params.page_name = pageName;
    params.page_value = pageValue;
    resetAccordionStates(params, page);

    if (isHome) {
      params.page_name = "home";
    }

    Object.assign(params, getFilterParamsFromState(stateParams));
    if (stateParams.search) {
      params.search = stateParams.search;
    }
    if (includeExternalAds) {
      params.include_external_ads = true;
    }

    params.dedupe_organic_from_ads = dedupeOrganicResultsFromAds;
    params.current_page_id = sessionStorage.getItem("current_page_id");

    Http(
      {
        method: "GET",
        url: "/api/retailer/product_lines",
        data: omitBy(params, x => {
          return x === null || x === undefined || x === "null" || x === "undefined";
        }),
      },
      setupProductLines,
      () => (productLines = []),
    );
  };

  let searchClassification: SearchClassification = null;
  const loadSearchClassification = params => {
    // The `st` param is a special param for brand family and product line search suggestion PLPs,
    // which include a `search` param in the URL but should not be treated like a true search PLP.
    if (stagedRolloutCheck("disco__did_you_mean_guidance") && params.search && !params.st) {
      Http(
        {
          method: "GET",
          url: "/api/retailer/search_classifications/classification_match",
          data: { search_term: params.search },
        },
        (data, _res) => (searchClassification = data.classification),
        () => (searchClassification = null),
      );
    } else {
      searchClassification = null;
    }
  };

  const setup = () => {
    stateParams = overrideStateParams || $params;
    if (!stateParams) {
      stateParams = {};
    }

    loadSearchClassification(stateParams);
    loadProductLines(stateParams);

    return true;
  };
  setup();

  // makes the page reload with the correct products when filters are applied
  $afterPageLoad(page => {
    setup();
    return true;
  });
</script>

<svelte:window on:scroll={storeScroll} />
<section class="product-listing-results fizz-styles">
  {#if aboveGridBanner}
    {#key aboveGridBanner}
      <BannerComponent banners={aboveGridBanner.banners} {mobileMode} />
    {/key}
  {/if}
  {#if !isHome}
    <Header
      hasProductLines={productLines.length && !loadingProductLines}
      page={pagination ? pagination.page : page}
      total={pagination ? pagination.totalEntries : productLines.length}
      perPage={pagination ? pagination.perPage : perPage}
    />
  {/if}
  {#key productLines}
    {#if productLines.length >= 8 && !loadingProductLines && searchClassification}
      <SearchClassificationGuidance
        bind:searchClassification
        bind:searchQuery={stateParams.search}
      />
    {/if}
    {#if productLines.length === 0 && !loadingProductLines}
      <NoProductLinesAvailable {stateParams} />
    {:else if isHome || productListLayout === ProductListingLayout.Grid}
      {#if !loadingProductLines}
        <ProductLineGrid
          {productLines}
          {stateParams}
          {gridBanners}
          shoppableUnit={shoppableUnitRes}
          trackProductIndexLayout={true}
          bind:mobileMode
        />
      {:else}
        <div class:fizz-container={isHome}>
          {#each Array(7) as _}
            <SkeletonProductCards perViewCards={4} />
          {/each}
        </div>
      {/if}
    {:else if productListLayout === ProductListingLayout.List}
      {#if !loadingProductLines}
        <ProductLineList
          {productLines}
          {stateParams}
          {Cart}
          banners={gridBanners}
          shoppableUnit={shoppableUnitRes}
          bind:mobileMode
        />
      {:else}
        {#each Array(7) as _}
          <SkeletonProductListCard />
        {/each}
      {/if}
    {/if}
    {#if productLines.length && !loadingProductLines}
      <div class="fizz-flex-row fizz-justify-center">
        <PaginationLarge {pagination} firstSixOnly={true} on:paginationEvent={resetScroll} />
      </div>
    {/if}
  {/key}

  <TrackingContext let:trackingContext>
    <div class="fizz-text-center fizz-gap-8 fizz-stack-16" style="display: grid;">
      <p>Can't find what you're looking for?</p>
      <a
        on:click|preventDefault={() => runShowAddProductModal(trackingContext.entities)}
        href={"#"}
        class="fizz-flex-row fizz-justify-center fizz-text-strong cursor-pointer add-missing-product-link"
      >
        Add Missing Product
      </a>
    </div>
  </TrackingContext>
</section>

<AddProductModal productLineId={null} bind:showModal={showAddProductModal} />
